import { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from "react";
import type { User, Session } from "@supabase/supabase-js";
import {
  getSession,
  getUser,
  onAuthStateChange,
  signOut as authSignOut,
  isMasterAccount,
  getAllowedPortals,
  fetchUserTenantRoles,
  fetchAllTenantsForMaster,
  getPortalsFromTenantRoles,
  TenantUserData,
  TenantUserRole,
} from "@/lib/auth";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isMaster: boolean;
  allowedPortals: string[];
  tenantRoles: TenantUserData[];
  currentTenant: TenantUserData | null;
  currentRole: TenantUserRole | null;
  setCurrentTenant: (tenant: TenantUserData | null) => void;
  signOut: () => Promise<void>;
  checkPortalAccess: (portal: string) => boolean;
  hasMultiplePortals: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tenantRoles, setTenantRoles] = useState<TenantUserData[]>([]);
  const [currentTenant, setCurrentTenant] = useState<TenantUserData | null>(null);

  const isMaster = isMasterAccount(user?.email);

  // Compute allowed portals from both static check and database roles
  const staticPortals = getAllowedPortals(user?.email);
  const dbPortals = getPortalsFromTenantRoles(tenantRoles);
  const allowedPortals = isMaster
    ? staticPortals
    : [...new Set([...staticPortals, ...dbPortals])];

  const hasMultiplePortals = allowedPortals.length > 1;
  const currentRole = currentTenant?.role ?? null;

  // Fetch tenant roles when user changes
  useEffect(() => {
    const loadTenantRoles = async () => {
      if (!user?.id) {
        setTenantRoles([]);
        return;
      }

      let roles: TenantUserData[];

      if (isMaster) {
        // Master accounts get access to all tenants
        roles = await fetchAllTenantsForMaster();
      } else {
        // Regular users get their assigned tenant roles
        roles = await fetchUserTenantRoles(user.id);
      }

      setTenantRoles(roles);

      // Auto-select first tenant if only one (or first for master accounts)
      if (roles.length >= 1 && !currentTenant) {
        setCurrentTenant(roles[0]);
      }
    };

    loadTenantRoles();
  }, [user?.id, isMaster]);

  useEffect(() => {
    // Get initial session
    const initAuth = async () => {
      try {
        const currentSession = await getSession();
        setSession(currentSession);

        if (currentSession) {
          const currentUser = await getUser();
          setUser(currentUser);
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error("Error initializing auth:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Memoize callbacks to prevent unnecessary re-renders
  const signOut = useCallback(async () => {
    try {
      await authSignOut();
      setUser(null);
      setSession(null);
      setTenantRoles([]);
      setCurrentTenant(null);
    } catch (error) {
      if (import.meta.env.DEV) console.error("Error signing out:", error);
      throw error;
    }
  }, []);

  const checkPortalAccess = useCallback((portal: string): boolean => {
    if (isMaster) return true;
    return allowedPortals.includes(portal);
  }, [isMaster, allowedPortals]);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  // This is critical to prevent infinite render loops in components that use useAuth
  const contextValue = useMemo(() => ({
    user,
    session,
    isLoading,
    isMaster,
    allowedPortals,
    tenantRoles,
    currentTenant,
    currentRole,
    setCurrentTenant,
    signOut,
    checkPortalAccess,
    hasMultiplePortals,
  }), [
    user,
    session,
    isLoading,
    isMaster,
    allowedPortals,
    tenantRoles,
    currentTenant,
    currentRole,
    signOut,
    checkPortalAccess,
    hasMultiplePortals,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
